import type { SedeConfig } from '../../sedi/types'
import { esc, meta as metaTag, prop } from '../../sedi/seo-render'
import type { Locale, PageKey, SeoCopy, ServiceKey, SiteCopy } from './content/types'
import { jsonLdFor, type OwnCredit } from './jsonld'
import { absoluteUrl, ogImagePath, SITE_ORIGIN, type RouteDef } from './routes'
import { srcSetFor } from './components/media/srcset'
import { SERVICE_MEDIA_ROLE } from './data/services'

/**
 * Per-route <head> for Verde Vercelli 2.
 *
 * `renderRouteHead` produces the head string the prerender step writes into
 * each generated index.html — that is the head crawlers actually read, since
 * they do not run our router. `applyHeadClient` only patches the live DOM
 * after a client-side navigation, so a visitor clicking through the site
 * still sees the right title and canonical.
 *
 * NOTE: this module is imported by the Node prerender. It must stay free of
 * DOM access at module scope — `applyHeadClient` touches `document` only
 * when it is called, and guards for a missing one.
 */

export type { OwnCredit }

export interface HeadMeta {
  lang: Locale
  title: string
  description: string
  canonical: string
  alternates: readonly { hrefLang: 'it' | 'en' | 'x-default'; href: string }[]
  ogImage: string
  ogImageAlt: string
  ogLocale: string
  ogLocaleAlternate: string
}

const OG_LOCALE: Record<Locale, string> = { it: 'it_IT', en: 'en_GB' }

/** Every prerendered route's robots value; the client 404 swaps it for NOINDEX and a matched route restores it. */
const ROBOTS_INDEX = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
const ROBOTS_NOINDEX = 'noindex, follow'

/** `/media/verde-vercelli-2/og/<key>-<locale>.jpg` — see routes.ts for why it lives there. */
export { ogImagePath }

type NonServicePageKey = Exclude<PageKey, ServiceKey>

function seoCopyFor(route: RouteDef, copy: SiteCopy): SeoCopy {
  if (route.serviceKey) return copy.pages[route.serviceKey].seo
  // Non-service route keys are exactly the non-service SEO keys minus 'notFound',
  // which has no RouteDef; the cast drops the service keys the compiler still sees.
  return copy.seo[route.key as NonServicePageKey]
}

/**
 * The photo role of the page's full-width, above-the-fold hero (spec §5):
 * the home hero, each service hero, and the zone hero (the same poster as
 * the home's). Other pages open on type, so they preload nothing. The
 * hero `<Picture>` must use `sizes="100vw"` to match
 * the preload's `imagesizes`, or the browser fetches the image twice.
 */
function heroRoleFor(route: RouteDef): string | null {
  if (route.kind === 'home') return 'hero'
  if (route.serviceKey) return SERVICE_MEDIA_ROLE[route.serviceKey]
  if (route.kind === 'zone') return 'hero'
  return null
}

export function headMetaFor(route: RouteDef, locale: Locale, copy: SiteCopy): HeadMeta {
  const seo = seoCopyFor(route, copy)
  const other: Locale = locale === 'it' ? 'en' : 'it'
  return {
    lang: locale,
    title: seo.title,
    description: seo.description,
    canonical: absoluteUrl(route.paths[locale]),
    // x-default points at the Italian page: this is an Italian business and
    // Italian is what an unmatched visitor should land on.
    alternates: [
      { hrefLang: 'it', href: absoluteUrl(route.paths.it) },
      { hrefLang: 'en', href: absoluteUrl(route.paths.en) },
      { hrefLang: 'x-default', href: absoluteUrl(route.paths.it) },
    ],
    ogImage: SITE_ORIGIN + ogImagePath(route.key, locale),
    ogImageAlt: seo.ogAlt,
    // The dictionary owns its og:locale, but a locale still falling back to
    // the Italian copy must not claim it_IT while <html lang> says en.
    ogLocale: copy.meta.locale === locale ? copy.meta.ogLocale : OG_LOCALE[locale],
    ogLocaleAlternate: OG_LOCALE[other],
  }
}

/**
 * The full <head> body for one prerendered page: everything between
 * <head> and the bundle's own tags. Joined with the same indent
 * `renderSeoHead` uses, so the two are indistinguishable in the output.
 */
export function renderRouteHead(args: {
  route: RouteDef
  locale: Locale
  sede: SedeConfig
  copy: SiteCopy
  credits: readonly OwnCredit[]
}): string {
  const { route, locale, sede, copy, credits } = args
  const m = headMetaFor(route, locale, copy)
  const { geo } = sede.seo
  const lines: string[] = []

  // ─── Core ──────────────────────────────────────────────────────────
  lines.push(`<title>${esc(m.title)}</title>`)
  lines.push(metaTag('description', m.description))
  lines.push(metaTag('author', sede.brand.name))
  lines.push(metaTag('robots', ROBOTS_INDEX))
  lines.push(`<link rel="canonical" href="${esc(m.canonical)}" />`)
  if (sede.seo.googleVerification) {
    lines.push(metaTag('google-site-verification', sede.seo.googleVerification))
  }

  // ─── Language alternates ───────────────────────────────────────────
  for (const a of m.alternates) {
    lines.push(`<link rel="alternate" hreflang="${esc(a.hrefLang)}" href="${esc(a.href)}" />`)
  }

  // ─── LCP hero preload ──────────────────────────────────────────────
  // AVIF only: a browser without AVIF skips the preload on `type` and
  // falls back to the <picture> sources as usual.
  const heroRole = heroRoleFor(route)
  if (heroRole) {
    lines.push(
      `<link rel="preload" as="image" type="image/avif" imagesrcset="${esc(srcSetFor(heroRole, 'avif'))}" imagesizes="100vw" fetchpriority="high" />`,
    )
  }

  // ─── Geo ───────────────────────────────────────────────────────────
  lines.push(metaTag('geo.region', geo.region))
  lines.push(metaTag('geo.placename', geo.placename))
  lines.push(metaTag('geo.position', `${geo.lat};${geo.lon}`))
  lines.push(metaTag('ICBM', `${geo.lat}, ${geo.lon}`))
  lines.push(metaTag('language', locale === 'it' ? 'Italian' : 'English'))
  lines.push(metaTag('content-language', locale))

  // ─── Open Graph ────────────────────────────────────────────────────
  lines.push(prop('og:type', 'website'))
  lines.push(prop('og:title', m.title))
  lines.push(prop('og:description', m.description))
  lines.push(prop('og:url', m.canonical))
  lines.push(prop('og:site_name', copy.meta.siteName))
  lines.push(prop('og:locale', m.ogLocale))
  lines.push(prop('og:locale:alternate', m.ogLocaleAlternate))
  lines.push(prop('og:image', m.ogImage))
  lines.push(prop('og:image:width', '1200'))
  lines.push(prop('og:image:height', '630'))
  lines.push(prop('og:image:alt', m.ogImageAlt))

  // ─── Twitter Card ──────────────────────────────────────────────────
  lines.push(metaTag('twitter:card', 'summary_large_image'))
  lines.push(metaTag('twitter:title', m.title))
  lines.push(metaTag('twitter:description', m.description))
  lines.push(metaTag('twitter:image', m.ogImage))
  lines.push(metaTag('twitter:image:alt', m.ogImageAlt))

  // ─── Mobile / PWA ──────────────────────────────────────────────────
  // No theme-color here: this sede ships a single one in `extraHead` (the
  // light paper colour), placed before the theme boot script, which repaints
  // it dark on a saved dark choice; ThemeToggle rewrites it on a switch.
  // Browsers use the FIRST theme-color, so a second one emitted here would
  // come first and show its colour instead of the light paper.
  lines.push(metaTag('apple-mobile-web-app-capable', 'yes'))
  lines.push(metaTag('apple-mobile-web-app-title', sede.seo.appleTitle))

  // ─── JSON-LD ───────────────────────────────────────────────────────
  for (const doc of jsonLdFor(route, locale, sede, copy, credits)) {
    // '</' → '<\/' so a stray closing tag inside a string value cannot end
    // the script element early. Still valid JSON for the crawler.
    const json = JSON.stringify(doc, null, 2).replace(/<\//g, '<\\/')
    lines.push(`<script type="application/ld+json">\n${json}\n</script>`)
  }

  // ─── Per-sede extras (theme boot, fonts) — raw trusted config HTML ──
  // Last on purpose: the boot script must run after the metadata is parsed,
  // and the head test asserts this ordering.
  if (sede.extraHead) lines.push(...sede.extraHead)

  return lines.join('\n    ')
}

/** Creates the tag when the prerendered head did not carry it (dev SPA shell). */
function upsert(selector: string, create: () => HTMLElement): HTMLElement | null {
  const existing = document.head.querySelector<HTMLElement>(selector)
  if (existing) return existing
  const el = create()
  document.head.appendChild(el)
  return el
}

function setMetaContent(attr: 'name' | 'property', key: string, value: string): void {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute(attr, key)
    return m
  })
  el?.setAttribute('content', value)
}

/**
 * Client-side head sync after a router navigation. Crawlers read the
 * prerendered head and never reach this; it exists so a human clicking
 * through the site gets an honest title, canonical and share card.
 */
export function applyHeadClient(meta: HeadMeta): void {
  if (typeof document === 'undefined' || !document.head) return

  document.title = meta.title
  if (document.documentElement) document.documentElement.lang = meta.lang

  setMetaContent('name', 'description', meta.description)
  // Undo a previous client 404 (applyNotFoundHeadClient); a no-op otherwise.
  setMetaContent('name', 'robots', ROBOTS_INDEX)

  // Re-created when missing: the dev SPA shell, or a previous client 404.
  const canonical = upsert('link[rel="canonical"]', () => {
    const l = document.createElement('link')
    l.rel = 'canonical'
    return l
  })
  canonical?.setAttribute('href', meta.canonical)

  // Replace the alternates wholesale: a route change changes every href,
  // and a stale hreflang is worse than none.
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
  for (const a of meta.alternates) {
    const link = document.createElement('link')
    link.setAttribute('rel', 'alternate')
    link.setAttribute('hreflang', a.hrefLang)
    link.setAttribute('href', a.href)
    document.head.appendChild(link)
  }

  setMetaContent('property', 'og:url', meta.canonical)
  setMetaContent('property', 'og:title', meta.title)
  setMetaContent('property', 'og:description', meta.description)
  setMetaContent('property', 'og:locale', meta.ogLocale)
  setMetaContent('property', 'og:locale:alternate', meta.ogLocaleAlternate)
  setMetaContent('property', 'og:image', meta.ogImage)
  setMetaContent('property', 'og:image:alt', meta.ogImageAlt)
}

/**
 * Client-side head for a path outside the route table (the client-rendered
 * 404). Without it the previous page's head stays: its title, canonical and
 * hreflang, and an indexable robots tag on a page that does not exist.
 * `applyHeadClient` restores robots and the canonical on the next matched
 * route. No DOM test: vitest runs in Node, and this is the whole function.
 */
export function applyNotFoundHeadClient(args: { locale: Locale; copy: SiteCopy }): void {
  if (typeof document === 'undefined' || !document.head) return
  const { locale, copy } = args

  document.title = copy.seo.notFound.title
  if (document.documentElement) document.documentElement.lang = locale

  document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove())
  setMetaContent('name', 'robots', ROBOTS_NOINDEX)
  setMetaContent('name', 'description', copy.seo.notFound.description)
}
